import { T } from '@/i18n';
/**
 * Whether the workplace coordinates a manager typed look right.
 *
 * Counted on the production copy 2026-09-20: of seven companies, six have no coordinates and one
 * has the pair swapped, latitude 11.17 and longitude 60.14, which is the sea off West Africa. Two
 * plain number boxes labelled Breddegrad and Lengdegrad invite exactly that, and nothing said a
 * word about it.
 *
 * Nothing here refuses a value. Timefly may sell outside Norway and a hard limit would be a bug
 * the day it does; these drive a warning, not a validation. The same three functions live in
 * mobile/mobile-scheduler/utils/geo.ts, because the phone is not in this npm workspace.
 */

// `| undefined` explicitly, because this project runs with exactOptionalPropertyTypes and the
// swap check builds a Place out of another Place's fields.
export interface Place {
  latitude?: number | string | null | undefined;
  longitude?: number | string | null | undefined;
}

/** On the globe at all, and not the (0, 0) that an unset column coerces to. */
export function isUsablePlace(p: Place | null | undefined): boolean {
  if (!p) return false;
  const lat = Number(p.latitude);
  const lon = Number(p.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  if (p.latitude == null || p.longitude == null || p.latitude === '' || p.longitude === '') return false;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return false;
  return !(lat === 0 && lon === 0);
}

/** Roughly 57 to 81 north and 4 to 32 east, which is Norway with Svalbard on top. */
export function looksLikeNorway(p: Place): boolean {
  const lat = Number(p.latitude);
  const lon = Number(p.longitude);
  return Number.isFinite(lat) && Number.isFinite(lon) && lat >= 57 && lat <= 81 && lon >= 4 && lon <= 32;
}

/** True when the pair reads like Norway with the two numbers the wrong way round. */
export function looksSwapped(p: Place): boolean {
  return !looksLikeNorway(p) && looksLikeNorway({ latitude: p.longitude, longitude: p.latitude });
}

/** What to tell the manager about what they have typed, or null when it is fine. */
const blank = (v: unknown) => v == null || v === '';

export function placeWarning(p: Place | null | undefined): string | null {
  // An emptied input gives back '', and a company that has not got round to this yet is not
  // making a mistake. Only a half-filled pair is worth a word.
  if (!p || (blank(p.latitude) && blank(p.longitude))) return null;
  if (blank(p.latitude) || blank(p.longitude)) return T('Fyll ut begge, eller la begge stå tomme.');
  if (looksSwapped(p)) return T('Ser ut som breddegrad og lengdegrad har byttet plass.');
  if (!isUsablePlace(p)) return T('Koordinatene peker ikke på et sted. Kartet i appen viser da ingenting.');
  if (!looksLikeNorway(p)) return T('Dette punktet ligger utenfor Norge. Stemmer det?');
  return null;
}
